import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Film, Sparkles, Zap, ArrowRight } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { Reveal } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";

const SITE_ORIGIN = "https://fenny.se";
import { SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import { aeoGeo } from "@/lib/seo/schema";
const OG_IMAGE = SOCIAL_IMAGE_URL;

export const Route = createFileRoute("/en_/services")({
  head: () => ({
    meta: [
      { title: "Services — Video, subscription and campaigns | Fenny" },
      { name: "description", content: "Fenny's services for video content: Pulse (monthly subscription), Prime (custom productions) and Pilot to test our process with a single video. Fixed pricing." },
      { property: "og:title", content: "Services — Fenny" },
      { property: "og:description", content: "Pulse for continuous video. Prime for campaigns. Pilot to test." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en/services" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Services — Fenny" },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "language", content: "en" },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/en/services" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/services", name: "Services — Video, subscription and campaigns | Fenny", description: "Fenny's services for video content: Pulse (monthly subscription), Prime (custom productions) and Pilot to test our process with a single video. Fixed pricing."})) },
    ],
  }),
  component: EnServicesPage,
});

const offerings = [
  {
    eyebrow: "Pulse",
    title: "Video subscription for marketing teams",
    body: "Continuous video deliveries at a fixed monthly fee. Script, direction, shoot, edit, grade, sound mix and format adaptation — one service. Three tiers: Lite, Core, Pro.",
    bullets: [
      "From EUR 1,395/month (Pulse Lite)",
      "2–6+ clips per month depending on tier",
      "Formats for YouTube, Instagram, LinkedIn, TikTok",
      "Strategic planning and continuous optimisation",
    ],
    cta: { to: "/en/pulse", label: "Read about Pulse" },
    secondary: { to: "/en/pricing", label: "See pricing" },
    accent: "mint",
    icon: Zap,
    thumb: OG_IMAGE,
  },
  {
    eyebrow: "Prime",
    title: "Custom productions with big ambition",
    body: "One-off productions when you need something extra: brand film, commercials, events, launches. Same team as Pulse, same quality bar — packaged for the individual project.",
    bullets: [
      "Tailored scope and budget",
      "From approx EUR 14,000 per production",
      "Strategy, script, production, post in one",
      "Combines freely with a Pulse subscription",
    ],
    cta: { to: "/en/prime", label: "Read about Prime" },
    secondary: { to: "/en/contact", label: "Discuss your project" },
    accent: "peach",
    icon: Sparkles,
    thumb: "https://fenny.se/prime-stills/garageportexperten-fenny-6.jpg",
  },
  {
    eyebrow: "Pilot",
    title: "Try how we work — no lock-in",
    body: "A full-scale one-off production at a fixed price. You experience our whole process — from strategy to finished film — before deciding on a subscription.",
    bullets: [
      "EUR 2,795 one-off",
      "One main cut + one trailer",
      "Same crew and gear as Pulse",
      "Available once per client",
    ],
    cta: { to: "/en/contact", label: "Talk about a pilot" },
    secondary: { to: "/en/pricing", label: "Compare with Pulse" },
    accent: "mint",
    icon: Film,
    thumb: "https://fenny.se/prime-stills/recompany-1.jpg",
  },
] as const;

function EnServicesPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-40 pb-12 md:pt-48 md:pb-16">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_30%_20%,color-mix(in_oklab,var(--mint)_10%,transparent)_0%,transparent_50%),radial-gradient(ellipse_at_80%_70%,color-mix(in_oklab,var(--peach)_8%,transparent)_0%,transparent_50%)]" />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="max-w-3xl">
            <Reveal>
              <LivePulseBadge tone="mint" className="mb-6">
                Services · three ways in
              </LivePulseBadge>
            </Reveal>
            <KineticHeading
              text="Video as a service — in every format you need."
              className="text-5xl font-semibold leading-[1.02] text-butter md:text-7xl"
            />
            <Reveal delay={0.7}>
              <p className="mt-7 max-w-2xl text-pretty text-lg text-foreground/85 md:text-xl">
                Three ways to work with us, one shared quality bar. Pick freely between a continuous
                subscription, custom productions or a pilot where you test the process before deciding.
              </p>
            </Reveal>
            <Reveal delay={0.9}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Magnetic>
                  <Button to="/en/contact" variant="mint" arrow trackId="book_strategy_meeting" trackProps={{ location: "en_services_hero" }}>
                    Book strategy call
                  </Button>
                </Magnetic>
                <Button href="#offerings" variant="outline">See our services</Button>
                <Button to="/en/pricing" variant="ghost">Pricing</Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <Section id="offerings" className="pt-4 scroll-mt-28">
        <div className="space-y-6">
          {offerings.map((o, i) => {
            const Icon = o.icon;
            const accent = o.accent === "peach" ? "text-peach ring-peach/30 bg-peach/15" : "text-mint ring-mint/30 bg-mint/15";
            const glow = o.accent === "peach" ? "peach" : "mint";
            return (
              <Reveal key={o.title}>
                <TiltCard glow={glow as "mint" | "peach"} intensity={3}>
                  <article id={o.eyebrow.toLowerCase()} className="scroll-mt-28 grid gap-8 overflow-hidden rounded-3xl border border-white/10 bg-surface p-8 transition-colors hover:border-mint/30 md:grid-cols-[1.2fr_1fr_1.4fr] md:p-10">
                    <div>
                      <div className={`inline-flex h-12 w-12 items-center justify-center rounded-full ring-1 ${accent}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground">0{i + 1} · {o.eyebrow}</div>
                      <h2 className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-4xl">{o.title}</h2>
                    </div>
                    <div className="relative hidden overflow-hidden rounded-2xl md:block">
                      <img src={o.thumb} alt={`${o.title} — still from a Fenny ${o.eyebrow.toLowerCase()} production`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-surface/60 via-transparent to-transparent" />
                    </div>
                    <div>
                      <p className="text-pretty text-base leading-relaxed text-foreground/85 md:text-lg">{o.body}</p>
                      <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                        {o.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm text-foreground/85">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-7 flex flex-wrap gap-3">
                        <Button to={o.cta.to} variant={o.accent === "peach" ? "peach" : "mint"} arrow>{o.cta.label}</Button>
                        <Button to={o.secondary.to} variant="outline">{o.secondary.label}</Button>
                      </div>
                    </div>
                  </article>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Compare */}
      <Section className="bg-surface/30">
        <EyebrowHeading eyebrow="Side by side" title="Three services, one decision." lede="Quick overview of how Pulse, Prime and Pilot differ — and where they're the same." />
        <div className="mx-auto mt-12 max-w-5xl overflow-hidden rounded-3xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="px-5 py-4"></th>
                <th className="px-5 py-4 text-mint">Pulse</th>
                <th className="px-5 py-4 text-butter">Prime</th>
                <th className="px-5 py-4">Pilot</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {[
                ["Pricing", "From EUR 1,395/month", "Custom quote", "EUR 2,795 one-off"],
                ["Volume", "2–6+ clips / month", "1 large project", "1 shoot day"],
                ["Strategy", "Quarterly plan", "Full pre-production", "Brief workshop"],
                ["Commitment", "3 month rolling", "Per project", "Single purchase"],
                ["Best for", "Marketing teams", "Big campaigns", "First-time test"],
              ].map((row) => (
                <tr key={row[0]} className="bg-surface/40">
                  <td className="px-5 py-4 font-medium text-foreground/90">{row[0]}</td>
                  <td className="px-5 py-4 text-muted-foreground">{row[1]}</td>
                  <td className="px-5 py-4 text-muted-foreground">{row[2]}</td>
                  <td className="px-5 py-4 text-muted-foreground">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Magnetic>
            <Button to="/en/contact" variant="mint" arrow>Book strategy call</Button>
          </Magnetic>
          <Link to="/en" className="inline-flex items-center gap-2 text-sm text-foreground/70 underline-offset-4 hover:text-mint hover:underline">
            Back to overview <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>
    </div>
  );
}
